import { useState, useEffect } from 'react';
import { subscriptionsAPI, plansAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import toast from 'react-hot-toast';

const fmt = v => `R$ ${parseFloat(v || 0).toFixed(2).replace('.', ',')}`;

export default function Billing() {
  const { user } = useAuth();
  const [subStatus, setSubStatus]   = useState(null);
  const [plans, setPlans]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [form, setForm]             = useState({ plan_id: '', payment_type: 'monthly', method: 'pix', coupon_code: '' });
  const [coupon, setCoupon]         = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pixResult, setPixResult]   = useState(null);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    Promise.allSettled([plansAPI.list(), subscriptionsAPI.status()])
      .then(([pr, sr]) => {
        if (pr.status === 'fulfilled') {
          const list = pr.value.data.data || [];
          setPlans(list);
          const pid = String(user?.current_plan_id || list[0]?.id || '');
          if (pid) setF('plan_id', pid);
        }
        if (sr.status === 'fulfilled') setSubStatus(sr.value.data.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const selectedPlan  = plans.find(p => String(p.id) === form.plan_id);
  const basePrice     = parseFloat(selectedPlan?.[form.payment_type === 'yearly' ? 'price_yearly' : 'price_monthly'] || 0);
  const discountAmt   = coupon
    ? (coupon.type === 'percent' ? basePrice * coupon.value / 100 : Math.min(basePrice, coupon.value))
    : 0;
  const total         = Math.max(0, basePrice - discountAmt);

  const applyCoupon = async () => {
    const code = form.coupon_code.trim().toUpperCase();
    if (!code) return;
    setCouponLoading(true);
    try {
      const r = await subscriptionsAPI.validateCoupon({ code, plan_id: Number(form.plan_id), applies_to: form.payment_type });
      setCoupon(r.data.data);
      toast.success('Cupom aplicado!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Cupom inválido.');
      setCoupon(null);
    } finally { setCouponLoading(false); }
  };

  const submit = async () => {
    if (!form.plan_id) { toast.error('Selecione um plano.'); return; }
    setSubmitting(true);
    try {
      const payload = { plan_id: Number(form.plan_id), payment_type: form.payment_type, method: form.method };
      if (coupon) payload.coupon_code = form.coupon_code.trim().toUpperCase();
      const r = await subscriptionsAPI.create(payload);
      const pmt = r.data.data.payment;
      if (pmt?.checkout_url) { window.location.href = pmt.checkout_url; return; }
      if (pmt?.pix_qr_code || pmt?.pix_base64) { setPixResult(pmt); return; }
      toast.success('Assinatura criada! Aguardando confirmação do pagamento.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao processar. Tente novamente.');
    } finally { setSubmitting(false); }
  };

  const checkPayment = async () => {
    try {
      const r = await subscriptionsAPI.status();
      if (r.data.data.status === 'active') {
        toast.success('Pagamento confirmado! Recarregando...');
        setTimeout(() => window.location.reload(), 1200);
      } else {
        toast('Pagamento ainda não confirmado. Aguarde alguns minutos.', { icon: '⏳' });
      }
    } catch { toast.error('Erro ao verificar status.'); }
  };

  const trialDays = user?.trial_ends_at
    ? Math.max(0, Math.ceil((new Date(user.trial_ends_at) - Date.now()) / 86400000))
    : null;
  const hasActive  = subStatus?.status === 'active';
  const hasPending = subStatus?.status === 'pending';

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#6B7280' }}>Carregando...</div>
  );

  /* ── PIX screen ── */
  if (pixResult) return (
    <div style={{ maxWidth: 520, margin: '0 auto' }}>
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 16, padding: 32, textAlign: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>✅</div>
        <h2 style={{ fontWeight: 800, color: '#0F172A', marginBottom: 8 }}>Assinatura criada!</h2>
        <p style={{ color: '#6B7280', marginBottom: 24 }}>Escaneie o QR Code ou copie o código PIX para pagar:</p>

        {pixResult.pix_base64 && (
          <img src={`data:image/png;base64,${pixResult.pix_base64}`} alt="PIX QR Code"
            style={{ width: 200, height: 200, margin: '0 auto 20px', display: 'block', border: '1px solid #E5E7EB', borderRadius: 8 }} />
        )}

        {pixResult.pix_qr_code && (
          <>
            <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 8, padding: '10px 14px', fontSize: '.7rem', wordBreak: 'break-all', color: '#374151', marginBottom: 10, textAlign: 'left' }}>
              {pixResult.pix_qr_code}
            </div>
            <button onClick={() => { navigator.clipboard.writeText(pixResult.pix_qr_code); toast.success('Código copiado!'); }}
              style={{ padding: '9px 22px', background: '#059669', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', marginBottom: 16 }}>
              Copiar código PIX
            </button>
          </>
        )}

        {pixResult.expires_at && (
          <p style={{ fontSize: '.78rem', color: '#9CA3AF', marginBottom: 12 }}>
            Expira em: {new Date(pixResult.expires_at).toLocaleString('pt-BR')}
          </p>
        )}

        <p style={{ fontSize: '.83rem', color: '#6B7280', marginBottom: 20 }}>
          Sua conta será ativada automaticamente em instantes após o pagamento.
        </p>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={checkPayment}
            style={{ padding: '10px 20px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>
            Verificar pagamento
          </button>
          <button onClick={() => setPixResult(null)}
            style={{ padding: '10px 20px', border: '1.5px solid #E5E7EB', borderRadius: 8, background: '#fff', cursor: 'pointer' }}>
            Voltar
          </button>
        </div>
      </div>
    </div>
  );

  const lbl80 = { fontSize: '.8rem', fontWeight: 700, color: '#374151', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '.04em', display: 'block' };

  /* ── Main page ── */
  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#0F172A', marginBottom: 20 }}>Assinatura</h1>

      {/* Status Card */}
      <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        {user?.tenant_status === 'trial' && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: trialDays > 0 ? 10 : 0 }}>
              <span style={{ background: trialDays === 0 ? '#FEE2E2' : '#FEF3C7', color: trialDays === 0 ? '#DC2626' : '#92400E', borderRadius: 6, padding: '3px 10px', fontSize: '.8rem', fontWeight: 700 }}>
                ⏳ Período de Teste
              </span>
              <span style={{ fontSize: '.9rem', color: '#374151' }}>
                {trialDays === null ? '' : trialDays > 0 ? `${trialDays} dia${trialDays !== 1 ? 's' : ''} restante${trialDays !== 1 ? 's' : ''}` : 'Período encerrado'}
              </span>
            </div>
            {trialDays > 0 && (
              <div style={{ background: '#F3F4F6', borderRadius: 6, height: 6, overflow: 'hidden' }}>
                <div style={{ height: '100%', borderRadius: 6, background: trialDays <= 2 ? '#EF4444' : '#F59E0B', width: `${Math.min(100, (trialDays / 7) * 100)}%`, transition: 'width .5s' }} />
              </div>
            )}
          </>
        )}

        {hasActive && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ background: '#D1FAE5', color: '#059669', borderRadius: 6, padding: '3px 10px', fontSize: '.8rem', fontWeight: 700 }}>✓ Assinatura Ativa</span>
            </div>
            <div style={{ fontSize: '.9rem', color: '#374151' }}>
              Plano: <strong>{subStatus.plan_name}</strong> — {fmt(subStatus.amount)}/{subStatus.type === 'yearly' ? 'ano' : 'mês'}
              {subStatus.ends_at && (
                <span style={{ color: '#6B7280', marginLeft: 8 }}>
                  · Renova em {new Date(subStatus.ends_at).toLocaleDateString('pt-BR')}
                </span>
              )}
            </div>
          </div>
        )}

        {hasPending && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 6, padding: '3px 10px', fontSize: '.8rem', fontWeight: 700 }}>⏳ Aguardando pagamento</span>
            <button onClick={checkPayment}
              style={{ padding: '5px 14px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600, fontSize: '.8rem', cursor: 'pointer' }}>
              Verificar
            </button>
          </div>
        )}

        {!hasActive && !hasPending && user?.tenant_status !== 'trial' && (
          <div style={{ fontSize: '.9rem', fontWeight: 600, color: user?.tenant_status === 'cancelled' ? '#DC2626' : '#D97706' }}>
            {user?.tenant_status === 'cancelled' ? '❌ Assinatura cancelada' : '⚠️ Conta com pendência de pagamento'}
          </div>
        )}
      </div>

      {/* Subscription Form */}
      {!hasActive && (
        <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 24 }}>
          <div style={{ fontWeight: 700, color: '#0F172A', marginBottom: 20, fontSize: '1rem' }}>
            {user?.tenant_status === 'trial' ? 'Assinar agora' : 'Renovar assinatura'}
          </div>

          {/* Plan selection */}
          <div style={{ marginBottom: 18 }}>
            <label style={lbl80}>Plano</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {plans.map(p => (
                <div key={p.id} onClick={() => setF('plan_id', String(p.id))}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', border: `2px solid ${form.plan_id === String(p.id) ? '#2563EB' : '#E5E7EB'}`, borderRadius: 10, cursor: 'pointer', background: form.plan_id === String(p.id) ? '#EFF6FF' : '#fff' }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${form.plan_id === String(p.id) ? '#2563EB' : '#D1D5DB'}`, background: form.plan_id === String(p.id) ? '#2563EB' : '#fff', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <span style={{ fontWeight: 700, color: '#0F172A' }}>{p.name}</span>
                    {p.description && <span style={{ color: '#6B7280', fontSize: '.8rem', marginLeft: 8 }}>{p.description}</span>}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, color: '#2563EB', fontSize: '1.05rem' }}>
                      {fmt(form.payment_type === 'yearly' ? p.price_yearly : p.price_monthly)}
                    </div>
                    <div style={{ fontSize: '.7rem', color: '#9CA3AF' }}>/{form.payment_type === 'yearly' ? 'ano' : 'mês'}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Period */}
          <div style={{ marginBottom: 18 }}>
            <label style={lbl80}>Período</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['monthly', 'Mensal'], ['yearly', 'Anual  (≈ 17% off)']].map(([v, l]) => (
                <div key={v} onClick={() => { setF('payment_type', v); setCoupon(null); }}
                  style={{ flex: 1, padding: '10px', border: `2px solid ${form.payment_type === v ? '#2563EB' : '#E5E7EB'}`, borderRadius: 8, cursor: 'pointer', textAlign: 'center', background: form.payment_type === v ? '#EFF6FF' : '#fff', fontSize: '.85rem', fontWeight: form.payment_type === v ? 700 : 400, color: form.payment_type === v ? '#2563EB' : '#374151' }}>
                  {l}
                </div>
              ))}
            </div>
          </div>

          {/* Coupon */}
          <div style={{ marginBottom: 18 }}>
            <label style={lbl80}>Cupom de desconto (opcional)</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={form.coupon_code}
                onChange={e => { setF('coupon_code', e.target.value.toUpperCase()); setCoupon(null); }}
                placeholder="CÓDIGO"
                style={{ flex: 1, padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, outline: 'none', fontSize: '.9rem', letterSpacing: '.06em', textTransform: 'uppercase' }}
              />
              <button onClick={applyCoupon} disabled={couponLoading}
                style={{ padding: '9px 16px', background: '#F3F4F6', border: '1.5px solid #E5E7EB', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: '.85rem', flexShrink: 0 }}>
                {couponLoading ? '...' : 'Aplicar'}
              </button>
            </div>
            {coupon && (
              <div style={{ marginTop: 6, fontSize: '.8rem', color: '#059669', fontWeight: 600 }}>
                ✓ {coupon.description || (coupon.type === 'percent' ? `${coupon.value}% de desconto` : `${fmt(coupon.value)} de desconto`)}
              </div>
            )}
          </div>

          {/* Payment method */}
          <div style={{ marginBottom: 20 }}>
            <label style={lbl80}>Forma de pagamento</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[
                ['pix', '💠 PIX', 'Instantâneo'],
                ['credit_card', '💳 Cartão', form.payment_type === 'monthly' ? 'Débito automático mensal' : 'Pagamento único (Mercado Pago)']
              ].map(([v, l, sub]) => (
                <div key={v} onClick={() => setF('method', v)}
                  style={{ flex: 1, padding: '12px', border: `2px solid ${form.method === v ? '#2563EB' : '#E5E7EB'}`, borderRadius: 8, cursor: 'pointer', background: form.method === v ? '#EFF6FF' : '#fff' }}>
                  <div style={{ fontWeight: 700, color: form.method === v ? '#2563EB' : '#0F172A', fontSize: '.9rem' }}>{l}</div>
                  <div style={{ fontSize: '.72rem', color: '#6B7280', marginTop: 2 }}>{sub}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Total */}
          <div style={{ background: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: 8, padding: '14px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '.82rem', color: '#6B7280' }}>Total a pagar</div>
              {coupon && discountAmt > 0 && (
                <div style={{ fontSize: '.78rem', color: '#9CA3AF', textDecoration: 'line-through' }}>{fmt(basePrice)}</div>
              )}
            </div>
            <div style={{ fontWeight: 900, fontSize: '1.6rem', color: '#0F172A' }}>{fmt(total)}</div>
          </div>

          <button onClick={submit} disabled={submitting}
            style={{ width: '100%', padding: '13px', background: submitting ? '#93C5FD' : '#2563EB', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: '1rem', cursor: submitting ? 'not-allowed' : 'pointer' }}>
            {submitting ? 'Processando...' : form.method === 'pix' ? '💠 Gerar código PIX' : '💳 Ir para pagamento'}
          </button>
        </div>
      )}
    </div>
  );
}
